/**
 * The stopwatch as pure data (feature plan 0071): no DOM, no storage, the clock passed in.
 *
 * `elapsedMs` is what has run up to the last pause; `runningSince` the wall-clock time (`Date.now()`) of the
 * last start, `null` while paused. Wall-clock rather than `performance.now()` on purpose: the state lives in
 * `localStorage` and must still be right after a reload, where a monotonic clock starts again at zero.
 * The price is a clock set backwards; `elapsed` never goes below what was already counted.
 */
export interface StopwatchState {
    readonly elapsedMs: number;
    readonly runningSince: number | null;
}

/** The displayed parts: hours, minutes, seconds, hundredths — hundredths CUT, not rounded (59.996 s shows
 *  59,99, never 60,00). Hours have no upper bound. */
export interface StopwatchParts {
    readonly h: number;
    readonly m: number;
    readonly s: number;
    readonly cs: number;
}

export const INITIAL_STOPWATCH: StopwatchState = {elapsedMs: 0, runningSince: null};

export function isRunning(state: StopwatchState): boolean {
    return state.runningSince !== null;
}

export function elapsed(state: StopwatchState, now: number): number {
    if (state.runningSince === null) {
        return state.elapsedMs;
    }
    return state.elapsedMs + Math.max(0, now - state.runningSince);
}

export function start(state: StopwatchState, now: number): StopwatchState {
    return state.runningSince === null ? {elapsedMs: state.elapsedMs, runningSince: now} : state;
}

export function pause(state: StopwatchState, now: number): StopwatchState {
    return state.runningSince === null ? state : {elapsedMs: elapsed(state, now), runningSince: null};
}

export function reset(): StopwatchState {
    return INITIAL_STOPWATCH;
}

export function parts(ms: number): StopwatchParts {
    const total = Math.max(0, Math.floor(ms / 10));
    return {
        h: Math.floor(total / 360000),
        m: Math.floor(total / 6000) % 60,
        s: Math.floor(total / 100) % 60,
        cs: total % 100,
    };
}

/** Two digits, `7` → `07`. */
export function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

/** The time as text: `mm:ss,cc`, from one hour on `h:mm:ss,cc`. Also the readable form for `aria-label`. */
export function formatTime(p: StopwatchParts, withHundredths = true): string {
    const head = p.h > 0 ? `${p.h}:${pad2(p.m)}:${pad2(p.s)}` : `${pad2(p.m)}:${pad2(p.s)}`;
    return withHundredths ? `${head},${pad2(p.cs)}` : head;
}

/**
 * The running part of a stored value — elapsed time and start — or `null` when it is not one: wrong types,
 * negative or non-finite numbers. A start lying in the future (a clock set back since) is treated as "now".
 * Shared with the timer (feature plan 0073), which must tell "invalid" from "valid and empty".
 */
export function parseRun(raw: unknown, now: number): StopwatchState | null {
    if (typeof raw !== 'object' || raw === null) {
        return null;
    }
    const {elapsedMs, runningSince} = raw as Record<string, unknown>;
    if (typeof elapsedMs !== 'number' || !Number.isFinite(elapsedMs) || elapsedMs < 0) {
        return null;
    }
    if (runningSince === null) {
        return {elapsedMs, runningSince: null};
    }
    if (typeof runningSince !== 'number' || !Number.isFinite(runningSince) || runningSince < 0) {
        return null;
    }
    return {elapsedMs, runningSince: Math.min(runningSince, now)};
}

/** A stored value as a usable state: anything that is not one is the initial state (see `parseRun`). */
export function sanitize(raw: unknown, now: number): StopwatchState {
    return parseRun(raw, now) ?? INITIAL_STOPWATCH;
}
